import type { ReactNode } from 'react'
import { CopyTableButtons } from './CopyTableButtons'

/** Split markdown into paragraphs and GFM-style tables for chat bubbles. */
export function ChatMarkdown({ content, inverted = false }: { content: string; inverted?: boolean }) {
  const blocks = splitBlocks(content)
  const textClass = inverted ? 'text-white' : 'text-es-body'
  const mutedClass = inverted ? 'text-white/80' : 'text-es-muted'
  const borderClass = inverted ? 'border-white/30' : 'border-es-soft'
  const headClass = inverted ? 'bg-white/10' : 'bg-es-haze'

  return (
    <div className={`text-sm leading-relaxed space-y-3 ${textClass}`}>
      {blocks.map((block, i) => {
        if (block.type === 'table') {
          return (
            <CopyableTable
              key={i}
              headers={block.headers}
              rows={block.rows}
              inverted={inverted}
              borderClass={borderClass}
              headClass={headClass}
            />
          )
        }
        return (
          <p key={i} className={`whitespace-pre-wrap ${block.text.startsWith('Error:') ? mutedClass : ''}`}>
            {block.text}
          </p>
        )
      })}
    </div>
  )
}

function CopyableTable({
  headers,
  rows,
  inverted,
  borderClass,
  headClass,
}: {
  headers: string[]
  rows: string[][]
  inverted: boolean
  borderClass: string
  headClass: string
}) {
  const payload = { headers, rows }
  return (
    <div className="space-y-1.5 -mx-1">
      <div className="flex justify-end px-1">
        <CopyTableButtons payload={payload} inverted={inverted} />
      </div>
      <div className="overflow-x-auto">
        <table className={`min-w-full text-xs border ${borderClass} rounded`}>
          <thead className={headClass}>
            <tr>
              {headers.map((h, hi) => (
                <th
                  key={hi}
                  className={`px-2 py-1.5 text-left font-semibold border-b ${borderClass} whitespace-nowrap`}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, ri) => (
              <tr key={ri} className={inverted ? 'border-white/20' : 'border-es-soft'}>
                {row.map((cell, ci) => (
                  <td key={ci} className={`px-2 py-1.5 border-t ${borderClass} align-top`}>
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

type Block =
  | { type: 'text'; text: string }
  | { type: 'table'; headers: string[]; rows: string[][] }

function splitBlocks(content: string): Block[] {
  const lines = content.replace(/\r\n/g, '\n').split('\n')
  const blocks: Block[] = []
  let i = 0
  let textBuf: string[] = []

  const flushText = () => {
    const text = textBuf.join('\n').trim()
    if (text) blocks.push({ type: 'text', text })
    textBuf = []
  }

  while (i < lines.length) {
    if (isTableHeaderRow(lines[i]) && i + 1 < lines.length && isTableSepRow(lines[i + 1])) {
      flushText()
      const headers = splitTableRow(lines[i])
      i += 2
      const rows: string[][] = []
      while (i < lines.length && isTableDataRow(lines[i])) {
        rows.push(padRow(splitTableRow(lines[i]), headers.length))
        i++
      }
      blocks.push({ type: 'table', headers, rows })
      continue
    }
    textBuf.push(lines[i])
    i++
  }
  flushText()
  return blocks.length ? blocks : [{ type: 'text', text: content }]
}

function isTableHeaderRow(line: string): boolean {
  const t = line.trim()
  return t.includes('|') && !/^[-|: ]+$/.test(t)
}

function isTableSepRow(line: string): boolean {
  const t = line.trim()
  if (!t.includes('|') && !t.includes('-')) return false
  return /^[\s|:-]+$/.test(t) && t.includes('-')
}

function isTableDataRow(line: string): boolean {
  const t = line.trim()
  return t.includes('|') && !isTableSepRow(t)
}

function splitTableRow(line: string): string[] {
  let t = line.trim()
  if (t.startsWith('|')) t = t.slice(1)
  if (t.endsWith('|')) t = t.slice(0, -1)
  return t.split('|').map((c) => c.trim())
}

function padRow(cells: string[], len: number): string[] {
  const out = cells.slice(0, len)
  while (out.length < len) out.push('')
  return out
}

/** Re-export for tests / tree-shaking friendliness */
export type ChatMarkdownNode = ReactNode
